// Phase 43.17 — Admin order edit modal (sezioni: Ritiro · Consegna · Pacco · Operativo · Prezzo)
// Modale grande con motivo modifica + force_override + storico.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, X, History, AlertTriangle, Trash2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatDateTime, STATUS_LABEL, STATUS_STEPS } from "@/lib/format";

const ALL_STATUSES = [...STATUS_STEPS, "cancelled"];

// Campi sensibili (frontend, allineati al backend)
const SENSITIVE = new Set([
  "pickup_struct", "pickup_address",
  "pickup_contact_name", "pickup_contact_phone", "pickup_phone_prefix",
  "delivery_struct", "delivery_address",
  "delivery_contact_name", "delivery_contact_phone", "delivery_phone_prefix",
  "package_description", "package_size", "package_notes",
  "status", "driver_id", "price",
]);

const CRITICAL = new Set([
  "pickup_struct", "pickup_address",
  "delivery_struct", "delivery_address",
  "package_description", "package_size", "package_notes",
]);

// Stati che richiedono force_override per campi critici
const OVERRIDE_STATES = new Set(["picking_up", "picked_up", "delivering"]);
const NOTES_ONLY_STATES = new Set(["delivered", "cancelled"]);
const WARN_DRIVER_STATES = new Set(["accepted"]);

const PACKAGE_SIZES = [
  { value: "small", label: "Piccolo" },
  { value: "medium", label: "Medio" },
  { value: "large", label: "Grande" },
  { value: "xl", label: "XL" },
];

const initialForm = (o) => ({
  // Ritiro
  pickup_street: o?.pickup_struct?.street || "",
  pickup_house: o?.pickup_struct?.house_number || "",
  pickup_zip: o?.pickup_struct?.zip || "",
  pickup_city: o?.pickup_struct?.city || "",
  pickup_canton: o?.pickup_struct?.canton || "",
  pickup_country: o?.pickup_struct?.country || "Svizzera",
  pickup_access_notes: o?.pickup_struct?.access_notes || "",
  pickup_contact_name: o?.pickup_contact_name || "",
  pickup_phone_prefix: o?.pickup_phone_prefix || "+41",
  pickup_contact_phone: o?.pickup_contact_phone || "",
  // Consegna
  delivery_street: o?.delivery_struct?.street || "",
  delivery_house: o?.delivery_struct?.house_number || "",
  delivery_zip: o?.delivery_struct?.zip || "",
  delivery_city: o?.delivery_struct?.city || "",
  delivery_canton: o?.delivery_struct?.canton || "",
  delivery_country: o?.delivery_struct?.country || "Svizzera",
  delivery_access_notes: o?.delivery_struct?.access_notes || "",
  delivery_contact_name: o?.delivery_contact_name || "",
  delivery_phone_prefix: o?.delivery_phone_prefix || "+41",
  delivery_contact_phone: o?.delivery_contact_phone || "",
  // Pacco
  package_description: o?.package_description || "",
  package_size: o?.package_size || "medium",
  package_notes: o?.package_notes || "",
  // Operativo
  status: o?.status || "pending",
  driver_id: o?.driver_id || "",
  admin_internal_notes: o?.admin_internal_notes || "",
  // Prezzo
  price: o?.price ?? 0,
});

const buildStruct = (form, prefix) => {
  const street = (form[`${prefix}_street`] || "").trim();
  const zip = (form[`${prefix}_zip`] || "").trim();
  const city = (form[`${prefix}_city`] || "").trim();
  if (!street && !zip && !city) return null;
  return {
    street: street || null,
    house_number: form[`${prefix}_house`] || null,
    zip: zip || null,
    city: city || null,
    canton: form[`${prefix}_canton`] || null,
    country: form[`${prefix}_country`] || "Svizzera",
    access_notes: form[`${prefix}_access_notes`] || null,
  };
};

export default function AdminOrderEditModal({ order, drivers, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialForm(order));
  const [reason, setReason] = useState("");
  const [forceOverride, setForceOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Phase 43.45 — Doppia conferma elimina ordine
  const [deleteStep, setDeleteStep] = useState(0); // 0=hidden, 1=warning, 2=final
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setForm(initialForm(order));
    setReason("");
    setForceOverride(false);
    setDeleteStep(0);
  }, [order]);

  const loadHistory = async () => {
    if (!order) return;
    try {
      const { data } = await api.get("/admin/audit-logs", {
        params: { target_type: "order", target_id: order.id, limit: 50 },
      });
      setHistory(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const changes = useMemo(() => {
    if (!order) return {};
    const c = {};
    const initial = initialForm(order);
    // Compare editable fields with their initial form values. Omitted fields
    // such as company must survive an unrelated edit or driver assignment.
    for (const prefix of ["pickup", "delivery"]) {
      const next = buildStruct(form, prefix);
      if (JSON.stringify(next) !== JSON.stringify(buildStruct(initial, prefix))) {
        c[`${prefix}_struct`] = next === null ? null : { ...order[`${prefix}_struct`], ...next };
      }
    }
    // ── Contatti
    const directFields = [
      "pickup_contact_name", "pickup_contact_phone", "pickup_phone_prefix",
      "delivery_contact_name", "delivery_contact_phone", "delivery_phone_prefix",
      "package_description", "package_size", "package_notes",
      "admin_internal_notes",
    ];
    for (const f of directFields) {
      if ((form[f] || "") !== (initial[f] || "")) c[f] = form[f] || null;
    }
    // ── Status
    if (form.status && form.status !== initial.status) c.status = form.status;
    // ── Driver
    if ((form.driver_id || "") !== (initial.driver_id || "")) c.driver_id = form.driver_id || null;
    // ── Price (override)
    const oldPrice = Number(order.price || 0);
    const newPrice = Number(form.price || 0);
    if (Math.abs(oldPrice - newPrice) > 0.001) c.price = newPrice;
    return c;
  }, [form, order]);

  const status = order?.status || "pending";
  const isNotesOnly = NOTES_ONLY_STATES.has(status);
  const needsOverride = OVERRIDE_STATES.has(status);
  const showDriverWarn = WARN_DRIVER_STATES.has(status) || OVERRIDE_STATES.has(status);

  const changedFields = Object.keys(changes);
  const hasSensitive = changedFields.some((k) => SENSITIVE.has(k));
  const hasCritical = changedFields.some((k) => CRITICAL.has(k));
  const blockedByNotesOnly = isNotesOnly && changedFields.some((k) => k !== "admin_internal_notes");
  const reasonRequired = hasSensitive && !reason.trim();
  const overrideRequired = needsOverride && hasCritical && !forceOverride;

  const save = async () => {
    if (changedFields.length === 0) {
      toast("Nessuna modifica da salvare.");
      return;
    }
    if (blockedByNotesOnly) {
      toast.error(`Ordine in stato "${STATUS_LABEL[status]}": solo le note admin sono modificabili.`);
      return;
    }
    if (hasSensitive && !reason.trim()) {
      toast.error("Motivo obbligatorio per campi sensibili.");
      return;
    }
    if (needsOverride && hasCritical && !forceOverride) {
      toast.error("Per modificare indirizzi/pacco a ordine già ritirato attiva l'override forte.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, {
        changes,
        reason: reason.trim() || null,
        force_override: forceOverride,
      });
      toast.success(
        `Salvate ${data.changed} modifiche${data.driver_impact ? " — driver verrà avvisato" : ""}.`
      );
      onSaved?.(data.order);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Phase 43.45 — Hard delete admin ordine (richiede backend DELETE /admin/orders/{id})
  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Ordine ${order.short_id || order.id?.slice(0,6)} eliminato.`);
      setDeleteStep(0);
      onSaved?.(null);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  };

  if (!order) return null;

  return (
    <>
      {/* Phase 43.45 — z-[100]/z-[101] per stare sopra Toaster sonner (z=9999 ma sotto)
          e qualunque header sticky/banner del layout */}
      <div
        className="fixed inset-0 bg-black/60 z-[100]"
        onClick={onClose}
        data-testid="admin-order-edit-backdrop"
      />
      <div
        className="fixed inset-0 z-[101] flex items-center justify-center p-2 sm:p-6 pointer-events-none"
        data-testid="admin-order-edit-modal"
      >
        <div className="w-full max-w-3xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto">
          {/* Header */}
          <div className="border-b border-dapnow-line px-5 py-4 flex items-center justify-between">
            <div className="min-w-0">
              <p className="overline text-[10px]">Modifica ordine</p>
              <p className="font-display text-lg font-bold tracking-tight font-mono">
                #{order.id.slice(0, 8).toUpperCase()}
              </p>
              <p className="text-xs text-dapnow-muted">Stato attuale: <strong>{STATUS_LABEL[status]}</strong></p>
            </div>
            <button
              onClick={onClose}
              data-testid="admin-order-edit-close"
              className="rounded-lg border border-dapnow-line h-9 w-9 inline-flex items-center justify-center hover:border-dapnow-blue"
              aria-label="Chiudi"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Status warnings */}
          {isNotesOnly && (
            <Banner kind="warn">
              Ordine in stato <strong>{STATUS_LABEL[status]}</strong>: solo le <strong>note admin interne</strong> sono modificabili.
            </Banner>
          )}
          {showDriverWarn && !isNotesOnly && (
            <Banner kind="info">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              Driver già assegnato. Le modifiche operative possono impattare la consegna in corso.
            </Banner>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            <Section title="Ritiro">
              <AddressBlock prefix="pickup" form={form} onField={onField} testidPrefix="order-edit-pickup" disabled={isNotesOnly} />
              <ContactBlock prefix="pickup" form={form} onField={onField} testidPrefix="order-edit-pickup" disabled={isNotesOnly} />
            </Section>

            <Section title="Consegna">
              <AddressBlock prefix="delivery" form={form} onField={onField} testidPrefix="order-edit-delivery" disabled={isNotesOnly} />
              <ContactBlock prefix="delivery" form={form} onField={onField} testidPrefix="order-edit-delivery" disabled={isNotesOnly} />
            </Section>

            <Section title="Pacco">
              <Field label="Descrizione">
                <input
                  data-testid="order-edit-package-description"
                  className="input-tall w-full" disabled={isNotesOnly}
                  value={form.package_description} onChange={(e) => onField("package_description", e.target.value)} />
              </Field>
              <Field label="Dimensione">
                <select
                  data-testid="order-edit-package-size"
                  className="input-tall w-full" disabled={isNotesOnly}
                  value={form.package_size} onChange={(e) => onField("package_size", e.target.value)}>
                  {PACKAGE_SIZES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Note pacco">
                <textarea
                  data-testid="order-edit-package-notes"
                  className="input-tall w-full min-h-[60px]" disabled={isNotesOnly}
                  value={form.package_notes} onChange={(e) => onField("package_notes", e.target.value)} />
              </Field>
            </Section>

            <Section title="Operativo">
              <Field label="Stato ordine">
                <select
                  data-testid="order-edit-status"
                  className="input-tall w-full" disabled={isNotesOnly}
                  value={form.status} onChange={(e) => onField("status", e.target.value)}>
                  {ALL_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </Field>
              <Field label="Driver assegnato">
                <select
                  data-testid="order-edit-driver"
                  className="input-tall w-full" disabled={isNotesOnly}
                  value={form.driver_id} onChange={(e) => onField("driver_id", e.target.value)}>
                  <option value="">— nessuno (in coda) —</option>
                  {(drivers || []).map((d) => (
                    <option key={d.id} value={d.id}>{`${d.name} (${d.email})`}</option>
                  ))}
                </select>
              </Field>
              <Field label="Note admin interne">
                <textarea
                  data-testid="order-edit-admin-notes"
                  className="input-tall w-full min-h-[60px]"
                  value={form.admin_internal_notes}
                  placeholder="Visibili solo agli admin."
                  onChange={(e) => onField("admin_internal_notes", e.target.value)} />
              </Field>
            </Section>

            <Section title="Prezzo">
              <p className="text-sm font-semibold" data-testid="order-edit-cash-due">
                Importo finale in denaro: {formatCHF(order.final_price ?? order.price ?? 0)}
              </p>
              <Field label="Override tariffa registrata (CHF)">
                <input
                  type="number" step="0.05" min="0"
                  data-testid="order-edit-price"
                  className="input-tall w-full" disabled={isNotesOnly}
                  value={form.price} onChange={(e) => onField("price", e.target.value)} />
              </Field>
              <p className="text-[11px] text-amber-700">
                La tariffa registrata può differire dall'importo in denaro dopo pacchetti o crediti.
                L'override modifica anche l'importo finale ed è registrato nello storico con il motivo;
                non esegue né riconcilia pagamenti, crediti o rimborsi.
              </p>
            </Section>

            {/* Reason + override */}
            {(hasSensitive || forceOverride) && (
              <Section title="Motivo modifica (obbligatorio se sensibile)">
                <input
                  data-testid="order-edit-reason"
                  className="input-tall w-full"
                  placeholder="Es: cliente ha chiamato per cambiare destinazione…"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Section>
            )}

            {needsOverride && hasCritical && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm space-y-2">
                <p className="font-semibold text-amber-900">⚠ Override forte richiesto</p>
                <p className="text-xs text-amber-800">
                  Stai modificando indirizzi/pacco su un ordine già <strong>{STATUS_LABEL[status]}</strong>. Conferma il forte override esplicito.
                </p>
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="order-edit-force-override"
                    checked={forceOverride}
                    onChange={(e) => setForceOverride(e.target.checked)}
                  />
                  Confermo override admin (auditato).
                </label>
              </div>
            )}

            <Section
              title={
                <button
                  type="button"
                  onClick={() => { const next = !showHistory; setShowHistory(next); if (next) loadHistory(); }}
                  data-testid="order-edit-toggle-history"
                  className="inline-flex items-center gap-2 text-left"
                >
                  <History className="h-4 w-4" /> Storico modifiche {showHistory ? "▾" : "▸"}
                </button>
              }
            >
              {showHistory && (
                <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="order-edit-history-list">
                  {history.length === 0 && <p className="text-xs text-dapnow-muted">Nessuna modifica registrata.</p>}
                  {history.map((h) => (
                    <div key={h.id} className="text-xs border border-dapnow-line rounded-lg p-2">
                      <p className="font-semibold">
                        {h.field}
                        {h.force_override && <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px]">OVERRIDE</span>}
                      </p>
                      <p className="text-dapnow-muted">
                        <span className="line-through">{formatVal(h.old_value)}</span>
                        {" → "}
                        <span className="text-dapnow-ink">{formatVal(h.new_value)}</span>
                      </p>
                      <p className="text-dapnow-muted mt-1">
                        {h.admin_email || h.admin_name} · {formatDateTime(h.created_at)}
                      </p>
                      {h.reason && <p className="italic text-dapnow-muted">"{h.reason}"</p>}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          {/* Footer */}
          <div className="border-t border-dapnow-line px-5 py-3 flex items-center justify-between gap-3 bg-white flex-wrap">
            <p className="text-xs text-dapnow-muted" data-testid="order-edit-summary">
              {changedFields.length === 0
                ? "Nessuna modifica"
                : `${changedFields.length} ${changedFields.length > 1 ? "campi" : "campo"}: ${changedFields.join(", ")}`}
            </p>
            <div className="flex gap-2 flex-wrap">
              {/* Phase 43.45 — Elimina ordine con doppia conferma */}
              <button
                onClick={() => setDeleteStep(1)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white text-red-700 px-3 h-10 text-sm font-semibold hover:bg-red-50"
                data-testid="order-edit-delete-open"
                type="button"
              >
                <Trash2 className="h-4 w-4" /> Elimina ordine
              </button>
              <button
                onClick={onClose}
                className="rounded-lg border border-dapnow-line px-3 h-10 text-sm font-semibold hover:bg-dapnow-bg"
                data-testid="order-edit-cancel"
              >
                Annulla
              </button>
              <button
                onClick={save}
                disabled={saving || changedFields.length === 0 || reasonRequired || overrideRequired || blockedByNotesOnly}
                className="inline-flex items-center gap-2 rounded-lg bg-dapnow-ink text-white px-4 h-10 text-sm font-semibold disabled:opacity-50"
                data-testid="order-edit-save"
                title={
                  reasonRequired ? "Motivo obbligatorio per campi sensibili"
                  : overrideRequired ? "Conferma l'override forte per indirizzi/pacco"
                  : blockedByNotesOnly ? "Solo le note admin sono modificabili"
                  : undefined
                }
              >
                <Save className="h-4 w-4" />
                {saving ? "Salvataggio…" : "Salva modifiche"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 43.45 — Confirm modal "Elimina ordine" a 2 step */}
      {deleteStep > 0 && (
        <>
          <div className="fixed inset-0 bg-black/70 z-[110]" data-testid="order-delete-backdrop" />
          <div
            className="fixed inset-0 z-[111] flex items-center justify-center p-3"
            role="dialog"
            aria-modal="true"
            data-testid="order-delete-modal"
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-5 border-b border-dapnow-line flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-display text-lg font-bold tracking-tight">
                    {deleteStep === 1 ? "Eliminare definitivamente l'ordine?" : "Confermi DAVVERO l'eliminazione?"}
                  </p>
                  <p className="text-xs text-dapnow-muted">Ordine {order.short_id || order.id?.slice(0, 6)}</p>
                </div>
              </div>
              <div className="p-5 space-y-3 text-sm">
                {deleteStep === 1 ? (
                  <>
                    <p>
                      Stai per eliminare l'ordine dal database. Questa operazione è{" "}
                      <strong className="text-red-700">irreversibile</strong>: tutti i dati associati (storia, log, payout pendenti)
                      verranno persi.
                    </p>
                    <p className="text-dapnow-muted">
                      Considera di usare invece <strong>Annulla ordine</strong> dalla pagina ordini per uno storico pulito.
                    </p>
                  </>
                ) : (
                  <p className="text-red-700 font-semibold">
                    Tap definitivo. Dopo aver cliccato "Conferma eliminazione" l'ordine sarà cancellato per sempre.
                  </p>
                )}
              </div>
              <div className="p-4 border-t border-dapnow-line flex items-center justify-between gap-2 bg-dapnow-bg/40">
                <button
                  type="button"
                  onClick={() => setDeleteStep(0)}
                  className="rounded-lg border border-dapnow-line px-3 h-10 text-sm font-semibold bg-white hover:bg-dapnow-bg"
                  data-testid="order-delete-cancel"
                >
                  Annulla
                </button>
                {deleteStep === 1 ? (
                  <button
                    type="button"
                    onClick={() => setDeleteStep(2)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 text-white px-4 h-10 text-sm font-bold hover:bg-red-700"
                    data-testid="order-delete-step1-next"
                  >
                    Procedi <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={doDelete}
                    disabled={deleting}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 text-white px-4 h-10 text-sm font-bold hover:bg-red-700 disabled:opacity-60"
                    data-testid="order-delete-confirm"
                  >
                    <Trash2 className="h-4 w-4" />
                    {deleting ? "Elimino…" : "Conferma eliminazione"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function AddressBlock({ prefix, form, onField, testidPrefix, disabled }) {
  return (
    <>
      <div className="grid grid-cols-[1fr_120px] gap-2">
        <Field label="Via">
          <input data-testid={`${testidPrefix}-street`} className="input-tall w-full" disabled={disabled}
            value={form[`${prefix}_street`]} onChange={(e) => onField(`${prefix}_street`, e.target.value)} />
        </Field>
        <Field label="N.">
          <input data-testid={`${testidPrefix}-house`} className="input-tall w-full" disabled={disabled}
            value={form[`${prefix}_house`]} onChange={(e) => onField(`${prefix}_house`, e.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-[100px_1fr_120px] gap-2">
        <Field label="CAP">
          <input data-testid={`${testidPrefix}-zip`} className="input-tall w-full" disabled={disabled}
            value={form[`${prefix}_zip`]} onChange={(e) => onField(`${prefix}_zip`, e.target.value)} />
        </Field>
        <Field label="Città">
          <input data-testid={`${testidPrefix}-city`} className="input-tall w-full" disabled={disabled}
            value={form[`${prefix}_city`]} onChange={(e) => onField(`${prefix}_city`, e.target.value)} />
        </Field>
        <Field label="Cantone">
          <input data-testid={`${testidPrefix}-canton`} className="input-tall w-full" disabled={disabled}
            value={form[`${prefix}_canton`]} onChange={(e) => onField(`${prefix}_canton`, e.target.value)} />
        </Field>
      </div>
      <Field label="Note accesso">
        <input data-testid={`${testidPrefix}-access-notes`} className="input-tall w-full" disabled={disabled}
          value={form[`${prefix}_access_notes`]} onChange={(e) => onField(`${prefix}_access_notes`, e.target.value)} />
      </Field>
    </>
  );
}

function ContactBlock({ prefix, form, onField, testidPrefix, disabled }) {
  return (
    <div className="grid grid-cols-[1fr_90px_1fr] gap-2">
      <Field label="Contatto">
        <input data-testid={`${testidPrefix}-contact-name`} className="input-tall w-full" disabled={disabled}
          value={form[`${prefix}_contact_name`]} onChange={(e) => onField(`${prefix}_contact_name`, e.target.value)} />
      </Field>
      <Field label="Prefisso">
        <select data-testid={`${testidPrefix}-phone-prefix`} className="input-tall w-full" disabled={disabled}
          value={form[`${prefix}_phone_prefix`]} onChange={(e) => onField(`${prefix}_phone_prefix`, e.target.value)}>
          <option value="+41">+41</option>
          <option value="+39">+39</option>
        </select>
      </Field>
      <Field label="Telefono">
        <input data-testid={`${testidPrefix}-contact-phone`} className="input-tall w-full" disabled={disabled}
          value={form[`${prefix}_contact_phone`]} onChange={(e) => onField(`${prefix}_contact_phone`, e.target.value)} />
      </Field>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border border-dapnow-line rounded-xl p-4">
      <p className="overline mb-3">{title}</p>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted">{label}</span>
      {children}
    </label>
  );
}

function Banner({ kind, children }) {
  const cls = kind === "warn"
    ? "bg-amber-50 border-amber-300 text-amber-900"
    : "bg-blue-50 border-blue-300 text-blue-900";
  return (
    <div className={`px-5 py-3 border-b text-sm ${cls}`}>
      {children}
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
